import { useEffect, FC } from 'react';
import { Avatar, Flex, Text } from '@chakra-ui/react';
import { AnimatePresence, motion, useAnimation, Variants } from 'framer-motion';
import { ColorProps, get } from 'styled-system';
import { useAuthContext, useUserContext } from 'context';
import Logo from 'assets/images/levo.png';
import { NavItem } from 'navigation';
import { theme } from 'theme';
import MotionFlex from '../MotionFlex';
import SideBarButton from './SideBarButton';
import SideBarItem from './SideBarItem';
import { MenuCont, RenderWrapper } from './styles';

type SideBarProps = ColorProps & {
  accentColor: string;
  borderColor?: string;
  closeOnNavigate?: boolean;
  color: string;
  hoverColor: string;
  navItems: NavItem[];
  tooltipBg?: string;
  tooltipColor?: string;
};

const SideBar: FC<SideBarProps> = ({
  accentColor,
  bg,
  borderColor,
  children,
  color,
  hoverColor,
  navItems,
  tooltipBg,
  tooltipColor,
}) => {
  const { drawerOpen = false, toggleDrawer } = useUserContext();

  const { user, userData } = useAuthContext();

  const controls = useAnimation();

  useEffect(() => {
    if (drawerOpen) {
      controls.start('open');
    } else {
      controls.start('closed');
    }
  }, [drawerOpen, controls]);

  const variants: Variants = {
    open: {
      x: 0,
      width: 250,
      transition: { staggerChildren: 0.2, delayChildren: 0.1, stiffness: 80 },
    },
    closed: {
      x: 0,
      width: 64,
      transition: {
        stiffness: 80,
        staggerDirection: -1,
        staggerChildren: 0.2,
        delay: 0,
      },
    },
  };

  return (
    <>
      <MenuCont
        bg={bg}
        flexDir="column"
        animate={controls}
        variants={variants}
        alignItems="flex-start"
        justifyContent="flex-start"
        initial={{ width: drawerOpen ? 250 : 64 }}
      >
        <Flex
          pl="20px"
          width="100%"
          height="80px"
          alignItems="center"
          borderBottomWidth={1}
          justifyContent="flex-start"
          borderColor={borderColor}
          py={2}
        >
          <SideBarButton
            color={color}
            open={drawerOpen}
            onClick={toggleDrawer}
          />
          <Flex pr={2} flex={1} justifyContent="flex-start">
            <AnimatePresence>
              {drawerOpen && (
                <motion.img
                  width="40%"
                  height="60px"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  src={Logo}
                  style={{ alignSelf: 'flex-start' }}
                />
              )}
            </AnimatePresence>
          </Flex>
        </Flex>
        {user && (
          <MotionFlex
            py={4}
            pr={4}
            pl="18px"
            width="100%"
            align="center"
            justify="flex-start"
            borderBottomWidth={1}
            borderBottomColor={borderColor}
            wordBreak="break-all"
            whileHover={{
              backgroundColor: get(theme, `colors.${hoverColor}`, 'pink'),
              cursor: 'pointer',
            }}
          >
            <Avatar
              mr={4}
              name={`${userData?.firstname}` || ''}
              src={userData?.photoURL || ''}
              size="sm"
            />
            <AnimatePresence>
              {drawerOpen && (
                <Text
                  color={color}
                  fontWeight="bold"
                  animate={{ opacity: 1 }}
                  initial={{ opacity: 0 }}
                  exit={{ opacity: 0, pointerEvents: 'none' }}
                >
                  {`${userData?.firstname}` || ''}
                </Text>
              )}
            </AnimatePresence>
          </MotionFlex>
        )}
        {navItems.map((props: any) => (
          <SideBarItem
            color={color}
            key={props.title}
            hoverColor={hoverColor}
            accentColor={accentColor}
            tooltipColor={tooltipColor}
            tooltipBg={tooltipBg}
            {...props}
          />
        ))}
      </MenuCont>
      <RenderWrapper
        animate
        className="render-wrapper"
        pl={drawerOpen ? '250px' : '64px'}
      >
        {children}
      </RenderWrapper>
    </>
  );
};

export default SideBar;

SideBar.defaultProps = {
  color: 'white',
  bg: 'custom.levoBlue',
  hoverColor: 'gray.800',
  borderColor: 'gray.700',
  accentColor: 'cyan.500',
};
